
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ShareForm from "./ShareForm";
import SuccessMessage from "./SuccessMessage";
import { useShareFolder } from "./useShareFolder";
import { ShareFolderDialogProps } from "./types";
import { supabase } from "@/integrations/supabase/client";

/**
 * Dialog for sharing a folder with other users
 */
const ShareFolderDialog: React.FC<ShareFolderDialogProps> = ({
  isOpen,
  onOpenChange,
  folderId,
  projectId,
  folderName
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [sharedEmail, setSharedEmail] = useState("");
  const [existingShares, setExistingShares] = useState([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);
  
  const {
    email,
    setEmail,
    isSubmitting,
    error,
    handleSubmit,
    resetForm
  } = useShareFolder(folderId, projectId, folderName);

  // Fetch existing shares when dialog opens
  useEffect(() => {
    if (isOpen && folderId) {
      fetchExistingShares();
    }
  }, [isOpen, folderId]);

  const fetchExistingShares = async () => {
    try {
      setIsLoadingShares(true);
      
      const { data, error } = await supabase
        .from("folder_shares")
        .select("id, shared_with_email, created_at")
        .eq("folder_id", folderId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      console.log("Existing shares for folder:", data);
      setExistingShares(data || []);
    } catch (err) {
      console.error("Error fetching folder shares:", err);
    } finally {
      setIsLoadingShares(false);
    }
  };

  const handleShareSuccess = (email: string) => {
    setSharedEmail(email);
    setShowSuccess(true);
    // Refresh the shares list
    fetchExistingShares();
  };

  const handleClose = () => {
    // Reset everything when dialog closes
    resetForm();
    setShowSuccess(false);
    setExistingShares([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            {showSuccess ? "Delning slutförd" : `Dela mappen "${folderName}"`}
          </DialogTitle>
        </DialogHeader>

        {showSuccess ? (
          <SuccessMessage email={sharedEmail} />
        ) : (
          <ShareForm
            onSubmit={(e) => {
              handleSubmit(e, handleShareSuccess);
            }}
            error={error}
            isSubmitting={isSubmitting}
            email={email}
            setEmail={setEmail}
            onClose={handleClose}
            existingShares={existingShares}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareFolderDialog;
